import React, {useState} from 'react';
import {useAuth0} from "@auth0/auth0-react";
import {SendRpc} from "../../rpcSender";
import './WellnessSurvey.css'
import {useNavigate} from "react-router-dom";
import {useProfile} from "../../UserProfileProvider";
import {
  ClientProfileProto, IClientProfileProto, IUpdateUserProfileRequest,
  IWellnessSurveyApiProto,
  space,
  UpdateUserProfileRequest,
  UpdateUserProfileResponse,
  WellnessSurveyApiProto
} from "../../compiled";
import {App2} from "../App2";
import {SurveyPage} from "../../components/survey/SurveyComponents";
import {TextSurveyInput} from "../../components/survey/TextSurveyInput";
import {MultiSelect} from "../../components/survey/MultiSelectInput";
import {RadioSelect} from "../../components/survey/RadioSelectInput";
import IErrorProto = space.kenko.proto.IErrorProto;
import {SurveyDropdownInput} from "../../components/survey/SurveyDropdownInput";

export const WellnessSurvey = () => {

  const {profile, setProfile} = useProfile();

  const {getIdTokenClaims} = useAuth0();

  const [currentStep, setCurrentStep] = useState(0);


  const [results, setResults] = useState<IClientProfileProto>(
      new ClientProfileProto()
  );

  // Field level errors.
  const [errors, setErrors] = useState<IErrorProto[]>([]);

  // A top-level error that sits near the "continue" button.
  const [validationError, setValidationError] = useState<string>();

  // If there was an RPC error sending the profile.
  const [updateProfileError, setUpdateProfileError] = useState('');

  const navigate = useNavigate();

  /**
   * @param surveyComplete false for incremental drafts; true will submit this for review.
   */

  const sendUpdateUserProfileRequest = () => {

    let request = new UpdateUserProfileRequest({
      profile: results
    });

    let encoded: Uint8Array = UpdateUserProfileRequest.encode(request).finish();

    SendRpc(getIdTokenClaims, 'update_profile', encoded)
        .then(value => {
          const response = UpdateUserProfileResponse.decode(value);
          if (!response.updatedProfile) {
            console.warn('[Survey] Profile send failure!', response);
            setUpdateProfileError('Server error updating profile');
            return;
          }

          setProfile(response.updatedProfile);
          navigate('/');

        })
        .catch(e => {
          console.warn('[Survey] Profile send failure!', e);
          setUpdateProfileError(e);
        });
  };

  const surveyPages: SurveyPage[] = [
    {
      title: 'What is your name?',
      content: <TextSurveyInput
          value={results.name || ''}
          onContinue={() => onContinue()}
          onChange={v => {
            setResults({
              ...results,
              name: v
            })
            return;
          }}/>,

      validate: () => {
        if (!results.name || !results.name.trim()) {
          setValidationError('Please enter your name')
          return false;
        }

        return true;
      },
    },
    {
      title: 'What is your birth month and year?',
      content: <div className={'SurveyHorizontalRow'}>

        <SurveyDropdownInput
            value={results.birthDate?.month}
            placeholder={'Enter month'}
            options={["January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"]
                .map((name, index) => {
                  return {
                    value: index + 1,
                    label: name
                  }
                })}
            onChange={v => {
              setResults({
                ...results,
                birthDate: {
                  ...results.birthDate,
                  month: v
                }
              })
              return;
            }}/>
        <TextSurveyInput
            placeholder={'Enter year'}
            inputType={'number'}
            min={1900}
            max={2020}
            value={results.birthDate?.year?.toString() || ''}
            onContinue={() => advancePage()}
            onChange={v => {
              setResults({
                ...results,
                birthDate: {
                  ...results.birthDate,
                  year: v ? parseInt(v) : 0,
                }
              })
            }}/>
      </div>,

      validate: () => {
        if (!results.birthDate?.month ||
            results.birthDate.month <= 0 ||
            results.birthDate.month > 12) {
          setValidationError("Please enter a month");
          return false;
        }

        if (!results.birthDate.year ||
            results.birthDate.year < 1900 ||
            results.birthDate.year > 2025) {
          setValidationError("Please enter a valid year");
          return false;
        }

        return true;
      },
    },

    {
      title: 'What is your gender?',
      content: <SurveyDropdownInput
          value={results.wellnessSurveyAnswers?.gender}
          placeholder={'Select...'}
          options={["Female", "Male", "Non-binary", "Prefer not to say"]
              .map(v => {
                return {
                  value: v,
                  label: v
                }
              })}
          onChange={v => {
            setResults({
              ...results,
              wellnessSurveyAnswers: {
                ...results.wellnessSurveyAnswers,
                gender: v
              }
            })
            return;
          }}/>,

      validate: () => {
        if (!results.wellnessSurveyAnswers?.gender) {
          setValidationError("Please choose one");
          return false;
        }

        return true;
      },
    },

    {
      title: 'What are your primary wellness goals and health concerns?',
      subtitle: ' (Select all that apply)',
      content: <MultiSelect value={results.wellnessSurveyAnswers?.wellnessGoals ?? []}
                            onChange={v => {
                              setResults({
                                ...results,
                                wellnessSurveyAnswers: {
                                  ...results.wellnessSurveyAnswers,
                                  wellnessGoals: v
                                }
                              });
                            }}
                            options={[
                              "Mental wellbeing",
                              "Chronic pain management",
                              "Hormonal and reproductive health (e.g., fertility, menopause)",
                              "Digestive and nutritional health",
                              "Immune and autoimmune support (e.g., allergies, autoimmune conditions)",
                              "Sleep and restorative health",
                              "Skin and dermatological health",
                              "Life stage–specific health (e.g., pediatric, senior health)",
                              "Longevity and preventative care",
                            ].map(item => {
                              return {value: item, label: <>{item}</>}
                            })}
      />,
      validate: () => true,
    },

    {
      title: 'What holistic health treatments are you most interested in?',
      subtitle: ' (Select all that apply)',
      content: <MultiSelect value={results.wellnessSurveyAnswers?.holisticHealthInterests ?? []}
                            onChange={v => {
                              setResults({
                                ...results,
                                wellnessSurveyAnswers: {
                                  ...results.wellnessSurveyAnswers,
                                  holisticHealthInterests: v
                                }
                              });
                            }}
                            options={[
                              {
                                name: "Chiropractic care",
                                description: 'Focuses on diagnosing and treating musculoskeletal disorders, primarily through manual adjustments and manipulations of the spine to relieve pain and improve function',
                              },
                              {
                                name: "Acupuncture",
                                description: 'Uses needles to stimulate specific points on the body.',
                              },
                              {
                                name: "Massage therapy",
                                description: 'Involves manipulating muscles and soft tissues to relieve tension and stress',
                              },
                              {
                                name: "Traditional Chinese Medicine",
                                description: 'Uses herbs, acupuncture, and practices like cupping and moxibustion to restore balance and harmony in the body',
                              },
                              {
                                name: "Meditation and mindfulness",
                                description: 'Involves focusing the mind to reduce stress and increase mental clarity',
                              },
                              {
                                name: "Mind-body practices",
                                description: 'Integrates mental and physical aspects to enhance overall health, such as yoga or tai chi',
                              },
                              {
                                name: "Homeopathy",
                                description: 'Uses small, diluted doses of natural substances',
                              },
                              {
                                name: "Naturopathy",
                                description: 'Uses natural remedies to promote healing, including diet, exercise, and lifestyle changes',
                              },
                              {
                                name: "Energy healing (e.g., Reiki, Qi Gong)",
                                description: 'Focuses on the body’s energy fields to promote healing and balance',
                              },
                              {
                                name: "Sound therapy",
                                description: 'Uses sound waves, vibrations, or music to promote healing and relaxation in the body',
                              },
                              {
                                name: "Ayurveda",
                                description: 'Focuses on balancing the body’s systems through diet, herbal treatment, and yoga',
                              },
                              {
                                name: "None of the above",
                                description: ''
                              },
                            ].map((item: { name: string, description: string }) => {
                              return {
                                value: item.name,
                                label: <span><b>{item.name}</b>{item.description && `- ${item.description}`}</span>
                              }
                            })}/>,
    },

    {
      title: 'How open are you to integrating holistic or Eastern modalities into your wellness routine?',
      content: <RadioSelect value={results.wellnessSurveyAnswers?.opennessToHolisticModalities ?? []}
                            onChange={v => {
                              setResults({
                                ...results,
                                wellnessSurveyAnswers: {
                                  ...results.wellnessSurveyAnswers,
                                  opennessToHolisticModalities: v
                                }
                              });
                            }}
                            options={[
                              "Very open",
                              "Somewhat open / curious",
                              "Not open at this time",
                            ].map(item => {
                              return {label: item, value: item}
                            })
                            }/>,
      validate: () => {
        if (!results.wellnessSurveyAnswers?.opennessToHolisticModalities) {
          setValidationError('Please select an option');
          return false;
        }

        return true;
      }
    },
    {
      disableHeader: true,
      content: <>
        <div className='SurveyInfoPage'>
          <h2>Thank you for sharing!</h2>
          <div>As a next step, please explore content and connect with practitioners on your homepage.
          </div>
          <div>
            {/* uh i guess clicking next here will trigger the last page RPC to save the profile
             then go to the homepage */}
            <button className={'ActionButton'} onClick={() => onContinue()}>
              Go to homepage
            </button>
          </div>
        </div>
      </>
    },
  ];

  /**
   * @return {int} the next visible step, or the array length if there are no further pages.
   * By returning the array length we know that the
   */
  const getNextVisibleStep = () => {
    for (let i = currentStep + 1; i < surveyPages.length; i++) {
      const page = surveyPages[i];
      // The function may be undefined; if so, defaults to true;
      if (!page.isPageVisible || page.isPageVisible()) {
        return i;
      }
    }

    return surveyPages.length;
  };

  /**
   * @return {int} The previous step or -1 if there is no previous step
   */
  const getPreviousVisibleStep = () => {
    for (let i = currentStep - 1; i >= 0; i--) {
      let surveyPage = surveyPages[i];
      if (!surveyPage.isPageVisible || surveyPage.isPageVisible()) {
        return i;
      }
    }

    return -1;
  };

  const page = surveyPages[currentStep];

  /**
   *
   * @param results the most recent survey results--- make sure not to grab the state
   *                variable if you have mutated it in this frame! pass a new copy.
   *                this is important when using radio buttons
   */
  const advancePage = () => {

    // Check for validation errors
    if (page.validate && !page.validate()) {
      return;
    }

    // We were able to advance the page, clear out this error.
    setValidationError(undefined);

    const nextStep = getNextVisibleStep();

    if (nextStep < surveyPages.length) {
      // There's still another page to go. Advance the step
      setCurrentStep(nextStep);

    } else {

      // This is the last step! Kick off the RPC. Do not advance the page.
      sendUpdateUserProfileRequest();
    }
  };

  const goBackAPage = () => {
    // This just clears out the RPC error when going back
    setUpdateProfileError('');
    setCurrentStep(getPreviousVisibleStep());
    setValidationError(undefined);
  };


  // This is a null parameter if there is no next step available which hides the button
  const onBackPress = () => {
    goBackAPage();
  };

  // This gets happen on 'OK' press AND 'enter' press in text box.
  const onContinue = () => {
    advancePage();
  };

  return <App2>
    <div className={'SurveyProgressBar'}>
      {new Array(5).fill(0).map((_, i) => {
        return (
            <div key={i} className={currentStep > i ?
                'SurveyProgressBarItem SurveyProgressBarItemSelected' :
                'SurveyProgressBarItem SurveyProgressBarItemNotSelected'}/>
        );
      })}
    </div>

    <div className={'AppSection WhiteSection'}>
      <div className={'AppSectionContent'}>
        <div className='SurveySection'>

          {!page.disableHeader && <>
            <div className={'SurveyHeader'}>
              <div className={'SurveyHeaderLeft'}>{page.pageHeaderOverride ?? 'PROFILE SETUP'}</div>

              {currentStep > 0 &&
                  // Not every page has a back button      
                  <div className={'SurveyHeaderButtonContainer'}>
                    <button className={'ActionButton Inverted'}
                            onClick={() => onBackPress()}>Back
                    </button>
                  </div>
              }

              <div className={'SurveyHeaderButtonContainer'}>
                <button className='ActionButton' onClick={onContinue}>
                  {(currentStep == surveyPages.length - 1) ? 'Done' : 'Continue'}
                </button>
              </div>
            </div>
          </>}

          {validationError &&
              <div className={'SurveyErrorHeader'}>
                <div className={'ErrorText'}>{validationError}</div>
              </div>
          }

          {page.title && <span className={'SurveyTitle'}>{page.title}</span>}

          {page.subtitle && <div className={'SurveySubtitle'}>{page.subtitle}</div>}

          {/* This renders the specific widget for the input [checklist, radio, text, etc.] */}
          {page.content}

          {updateProfileError &&
              (<div className={'SurveyError'}>Error building your profile: {updateProfileError.toString()}</div>)
          }

          {/*  <pre style={{fontSize: 'smaller'}}>*/}
          {/*{JSON.stringify(results, null, 2)}*/}
          {/*  </pre>*/}


        </div>
      </div>
    </div>
  </App2>
};
